import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { map, skipWhile, take, tap } from 'rxjs/operators';

import { handleCustomError } from '@core/api';
import { APP_CONFIG, AppConfig } from '@core/config';

import { initActions } from '../actions/init.actions';
import { APP_STATES } from '../enums/app.enum';
import { getAppStatus } from '../selectors/init.selectors';

/**
 * Defined route can be accessed by anyone (but wait till user is defined).
 */
@Injectable({ providedIn: 'root' })
export class AuthAllGuard {
  private store = inject(Store);

  canActivate() {
    return this.store.select(getAppStatus).pipe(
      skipWhile((status) => status === APP_STATES.IDLE),
      take(1),
      map(() => true)
    );
  }
}

/**
 * Defined route can be accessed only by authorized users.
 */
@Injectable({ providedIn: 'root' })
export class AuthAuthorizedGuard {
  public router = inject(Router);
  private store = inject(Store);
  private location = inject(Location);

  canActivate() {
    return this.store.select(getAppStatus).pipe(
      skipWhile((status) => status === APP_STATES.IDLE),
      take(1),
      tap((status) => {
        if (status === APP_STATES.CLIENT) {
          localStorage.setItem('last.location', this.location.path());
          this.router.navigate(['/login']);
        }
      }),
      map((status) => status === APP_STATES.USER)
    );
  }
}

/**
 * Defined route can be accessed only by unauthorized users.
 */
@Injectable({ providedIn: 'root' })
export class AuthUnauthorizedGuard {
  public router = inject(Router);
  private store = inject(Store);

  canActivate() {
    return this.store.select(getAppStatus).pipe(
      skipWhile((status) => status === APP_STATES.IDLE),
      take(1),
      tap((status) => {
        if (status === APP_STATES.USER) {
          this.router.navigate(['/maps']);
        }
      }),
      map((status) => status === APP_STATES.CLIENT || status === APP_STATES.ERROR)
    );
  }
}
/**
 * Defined route can be accessed only if SSO is enabled
 */
@Injectable({ providedIn: 'root' })
export class AuthSSOGuard {
  config = inject<AppConfig>(APP_CONFIG);
  private store = inject(Store);

  canActivate() {
    if (!this.config.featureFlags?.['sso']) {
      this.store.dispatch(
        initActions.appError({
          error: handleCustomError('Single sign-on is currently unavailable.'),
        })
      );
      return false;
    }

    return true;
  }
}
